import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, FileText, Code, Link, Mail, Archive } from 'lucide-react';
import WorkflowTabView from '../../../components/WorkflowTabView';
import PrePublishChecks from './PrePublishChecks';
import FinalPDF from './FinalPDF';
import XBRLPackage from './XBRLPackage';
import PublicLink from './PublicLink';
import DistributionList from './DistributionList';
import ComplianceArchive from './ComplianceArchive';

const PublishWorkflow = ({ previewOnly = false }) => {
  const { t } = useTranslation();
  
  // Load data from localStorage on component mount
  const loadFromStorage = () => {
    try {
      const savedData = localStorage.getItem('publishWorkflowData');
      const savedStep = localStorage.getItem('publishWorkflowStep');
      
      if (savedData) {
        return {
          formData: JSON.parse(savedData),
          currentStep: savedStep ? parseInt(savedStep) : 1
        };
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
    
    return {
      formData: {
        prePublishChecks: {},
        finalPDF: {},
        xbrlPackage: {},
        publicLink: {},
        distributionList: {},
        complianceArchive: {}
      },
      currentStep: 1
    };
  };

  const { formData: initialFormData, currentStep: initialStep } = loadFromStorage();
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState(initialFormData);

  // Define workflow steps
  const steps = [
    {
      id: 1,
      title: t('workflow.publish.steps.prePublishChecks.title'),
      description: t('workflow.publish.steps.prePublishChecks.description'),
      icon: CheckCircle
    },
    {
      id: 2,
      title: t('workflow.publish.steps.finalPDF.title'),
      description: t('workflow.publish.steps.finalPDF.description'),
      icon: FileText
    },
    {
      id: 3,
      title: t('workflow.publish.steps.xbrlPackage.title'),
      description: t('workflow.publish.steps.xbrlPackage.description'),
      icon: Code
    },
    {
      id: 4,
      title: t('workflow.publish.steps.publicLink.title'),
      description: t('workflow.publish.steps.publicLink.description'),
      icon: Link
    },
    {
      id: 5,
      title: t('workflow.publish.steps.distributionList.title'),
      description: t('workflow.publish.steps.distributionList.description'),
      icon: Mail
    },
    {
      id: 6,
      title: t('workflow.publish.steps.complianceArchive.title'),
      description: t('workflow.publish.steps.complianceArchive.description'),
      icon: Archive
    }
  ];

  // Save data to localStorage
  const saveToStorage = (newFormData, newStep) => {
    try {
      localStorage.setItem('publishWorkflowData', JSON.stringify(newFormData));
      localStorage.setItem('publishWorkflowStep', newStep.toString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const handleStepClick = (stepId) => {
    setCurrentStep(stepId);
    saveToStorage(formData, stepId);
  };

  const handleSubmit = (stepData) => {
    const newFormData = {
      ...formData,
      [getStepKey(currentStep)]: stepData
    };
    setFormData(newFormData);
    saveToStorage(newFormData, currentStep);
  };

  const handleNext = () => {
    if (currentStep < 6) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      saveToStorage(formData, nextStep);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      saveToStorage(formData, prevStep);
    }
  };

  const getStepKey = (step) => {
    switch (step) {
      case 1: return 'prePublishChecks';
      case 2: return 'finalPDF';
      case 3: return 'xbrlPackage';
      case 4: return 'publicLink';
      case 5: return 'distributionList';
      case 6: return 'complianceArchive';
      default: return 'prePublishChecks';
    }
  };

  const renderCurrentStep = () => {
    const currentData = formData[getStepKey(currentStep)];

    switch (currentStep) {
      case 1:
        return (
          <PrePublishChecks
            onSubmit={handleSubmit}
            onNext={handleNext}
            initialData={currentData}
          />
        );
      case 2:
        return (
          <FinalPDF
            onSubmit={handleSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            initialData={currentData}
          />
        );
      case 3:
        return (
          <XBRLPackage
            onSubmit={handleSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            initialData={currentData}
          />
        );
      case 4:
        return (
          <PublicLink
            onSubmit={handleSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            initialData={currentData}
          />
        );
      case 5:
        return (
          <DistributionList
            onSubmit={handleSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            initialData={currentData}
          />
        );
      case 6:
        return (
          <ComplianceArchive
            onSubmit={handleSubmit}
            onPrevious={handlePrevious}
            initialData={currentData}
          />
        );
      default:
        return <PrePublishChecks />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <WorkflowTabView
        steps={steps}
        currentStep={currentStep}
        onStepChange={handleStepClick}
        previewOnly={previewOnly}
        onComplete={() => {
          // Handle workflow completion
          console.log('Publish workflow completed');
        }}
      >
        {renderCurrentStep()}
      </WorkflowTabView>
    </div>
  );
};

export default PublishWorkflow;
